/**
 * Onboarding icon map.
 *
 * One semantic key per idea, so every wizard surface pulls the same glyph for
 * the same thing. Add a key here before using a new glyph anywhere in
 * `src/components/onboarding` or `src/pages/UnifiedOnboarding.tsx`.
 *
 * Every value is an export of `@hugeicons/core-free-icons` (verified against
 * the installed package, v3.3.0). Rendered only through `OnboardingIconChip`
 * at 16px stroke 1.5 in `text-muted-foreground`. Icons are never tinted: tone
 * lives in the dot and the number (DESIGN.md, "What never gets color").
 */

import {
  Alert01Icon,
  ArrowUpRight01Icon,
  Building03Icon,
  CheckmarkCircle01Icon,
  CodeIcon,
  Copy01Icon,
  Globe02Icon,
  Loading01Icon,
  Mail01Icon,
  PenTool03Icon,
  Rocket01Icon,
  ShoppingCart01Icon,
  Target02Icon,
  TestTube01Icon,
  UserGroupIcon,
  UserIcon,
} from "@hugeicons/core-free-icons"
import type { IconSvgElement } from "@hugeicons/react"

export const ONBOARDING_ICONS = {
  // Who is setting this up (profile step)
  "icp.marketing_team": UserGroupIcon,
  "icp.founder": UserIcon,
  "icp.agency": Building03Icon,
  "icp.creator": PenTool03Icon,

  // What they are growing (select-goal step)
  "goal.saas": Rocket01Icon,
  "goal.ecommerce": ShoppingCart01Icon,
  "goal.newsletter": Mail01Icon,
  "goal.agency": Building03Icon,

  // Rail steps
  "step.you": UserIcon,
  "step.goal": Target02Icon,
  "step.website": Globe02Icon,
  "step.plan": TestTube01Icon,
  "step.tracker": CodeIcon,

  // Machine state
  "state.waiting": Loading01Icon,
  "state.live": CheckmarkCircle01Icon,
  "state.failed": Alert01Icon,

  // Actions
  "action.copy": Copy01Icon,
  "action.send": Mail01Icon,

  // Data
  "proof.lift": ArrowUpRight01Icon,
} as const satisfies Record<string, IconSvgElement>

export type OnboardingIconKey = keyof typeof ONBOARDING_ICONS

/** Resolve a semantic key to its hugeicons glyph. */
export function onboardingIcon(key: OnboardingIconKey): IconSvgElement {
  return ONBOARDING_ICONS[key]
}

/** True when a value is a key of the map rather than a raw glyph. */
export function isOnboardingIconKey(value: unknown): value is OnboardingIconKey {
  return typeof value === "string" && value in ONBOARDING_ICONS
}
